import { useTranslation } from 'react-i18next';
import type { Transaction } from '@asia/shared';
import { fmtCoin, fmtUsd, fmtTime } from '../../lib/format';
import { FigIcon, type FigIconName } from '../FigIcon';
import { CoinIcon } from '../CoinIcon';

// Icon circle — #2195f1 base with a lighter top and an inner white glow, matching
// the Figma render (node 2:9279: bg #2195f1 + inset 0 0 5.6px rgba(255,255,255,0.56)).
const ICON_BG =
  'radial-gradient(100% 100% at 50% 18%, #5cb6f8 0%, #2195f1 55%, #1a8ce8 100%)';
const ICON_GLOW = 'inset 0px 0px 5.6px 0px rgba(255,255,255,0.56)';

const meta = (
  type: Transaction['type'],
): { icon: FigIconName; labelKey: string; pos: boolean } => {
  switch (type) {
    case 'receive':
      return { icon: 'receive', labelKey: 'history.received', pos: true };
    case 'send':
      return { icon: 'send', labelKey: 'history.sent', pos: false };
    case 'qr':
      return { icon: 'qr', labelKey: 'history.qrPayment', pos: false };
    case 'topup':
      return { icon: 'receive', labelKey: 'history.topUp', pos: true };
    case 'buy':
      return { icon: 'receive', labelKey: 'history.bought', pos: true };
    default:
      return { icon: 'send', labelKey: 'history.sent', pos: false };
  }
};

export function TransactionRow({
  tx,
  onClick,
  badge = 'TRC-20',
}: {
  tx: Transaction;
  onClick?: () => void;
  /** The small tag under the time. Defaults to the chain; card rows pass their
   *  merchant, or null — a card payment never touched TRC-20. */
  badge?: string | null;
}) {
  const { t } = useTranslation();
  const m = meta(tx.type);
  return (
    <button
      onClick={onClick}
      className="flex w-full items-center gap-[10px] px-4 py-3 text-left active:bg-press/[0.03]"
    >
      <span
        className="grid h-10 w-10 shrink-0 place-items-center rounded-full text-white"
        style={{ background: ICON_BG, boxShadow: ICON_GLOW }}
      >
        <FigIcon name={m.icon} size={24} />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-[16px] font-medium text-ink">{t(m.labelKey)}</p>
        <p className="mt-1 flex items-center gap-[6px] text-[14px] text-muted">
          {fmtTime(tx.date)}
          {badge && (
            <span
              className="max-w-[160px] truncate rounded-[4px] px-1 py-0.5 text-[10px] font-medium text-positive"
              style={{ backgroundColor: 'rgb(var(--positive) / 0.16)' }}
            >
              {badge}
            </span>
          )}
        </p>
      </div>
      <div className="text-right">
        <p
          className={`flex items-center justify-end gap-1 text-[16px] font-medium ${m.pos ? 'text-positive' : 'text-ink'}`}
        >
          {fmtCoin(tx.amount, tx.coin)}
          <CoinIcon symbol={tx.coin} size={16} />
        </p>
        <p className="mt-1 text-[14px] text-muted">{fmtUsd(tx.amountUsd)}</p>
      </div>
    </button>
  );
}
